import { useMemo } from "react";

import { useRequest } from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import { getAsset } from "~/v2/api/rest/assets";
import { ASSET_METADATA_STALE_TIME } from "~/v2/constants";

export const orderAssetNameQueryKeys = {
  all: [...queryKeys.assets.all, "atd-order-names"] as const,
  batch: (symbols: readonly string[]) =>
    [...orderAssetNameQueryKeys.all, ...symbols] as const,
};

const loadAssetNames = async (
  symbols: readonly string[],
): Promise<Record<string, string>> => {
  const entries = await Promise.all(
    symbols.map(async (symbol) => {
      try {
        const asset = await getAsset(encodeURIComponent(symbol));
        const name = asset.name?.trim();
        return name ? ([symbol, name] as const) : undefined;
      } catch {
        return undefined;
      }
    }),
  );

  return Object.fromEntries(
    entries.filter(
      (entry): entry is readonly [string, string] => entry !== undefined,
    ),
  );
};

export const useOrderAssetNames = (
  symbols: readonly string[],
): ReadonlyMap<string, string> => {
  const uniqueSymbols = useMemo(
    () => [...new Set(symbols.filter(Boolean))].sort(),
    [symbols],
  );
  const { data } = useRequest(
    orderAssetNameQueryKeys.batch(uniqueSymbols),
    () => loadAssetNames(uniqueSymbols),
    {
      enabled: uniqueSymbols.length > 0,
      staleTime: ASSET_METADATA_STALE_TIME,
      gcTime: 9999999,
      meta: { showErrorToast: false },
    },
  );

  return useMemo(() => {
    const names = new Map<string, string>();
    if (!data) {
      return names;
    }
    for (const [symbol, name] of Object.entries(data)) {
      names.set(symbol, name);
    }
    return names;
  }, [data]);
};
